import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { CountryDrawerData } from './types';

interface CountryImpactDrawerProps {
  open: boolean;
  data: CountryDrawerData | null;
  loading: boolean;
  keyAreaLabel?: string | null;
  onClose: () => void;
}

const CountryImpactDrawer: React.FC<CountryImpactDrawerProps> = ({
  open,
  data,
  loading,
  keyAreaLabel,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            aria-label="Close country panel"
            className="fixed inset-0 z-40 bg-black/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl border-l border-gray-200 flex flex-col"
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-[#f4faf9]">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-[#15736d] font-semibold">
                  Country impact
                  {keyAreaLabel ? ` · ${keyAreaLabel}` : ''}
                </p>
                <h2 className="text-xl font-bold text-gray-900">
                  {data?.country?.name || '…'}
                </h2>
                {data?.country?.is_member ? (
                  <span className="inline-block mt-1 text-[11px] font-semibold text-[#0f5b57] bg-[#d8ebe8] px-2 py-0.5 rounded">
                    EuFMD member
                  </span>
                ) : data?.country?.is_neighbour ? (
                  <span className="inline-block mt-1 text-[11px] font-semibold text-gray-700 bg-[#dce9e7] px-2 py-0.5 rounded">
                    Neighbouring country
                  </span>
                ) : data?.country ? (
                  <span className="inline-block mt-1 text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    Country
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {loading && (
                <div className="space-y-3 animate-pulse">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 rounded-lg bg-gray-100" />
                  ))}
                </div>
              )}

              {!loading && data?.empty && (
                <div className="rounded-xl border border-dashed border-[#b7d0cc] bg-[#f7fbfa] p-4 text-sm text-gray-600">
                  <p className="font-semibold text-[#15736d] mb-1">Training & activities</p>
                  <p>
                    {data.empty_message ||
                      'No activity details yet. Course and field evidence will appear here as six-month reports are ingested.'}
                  </p>
                </div>
              )}

              {!loading && data && !data.empty && (
                <section>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Recent activities</h3>
                  <ul className="space-y-3">
                    {data.activities.map((a) => (
                      <li
                        key={a.evidence_id}
                        className="rounded-lg border border-gray-150 border-gray-200 p-3 bg-white"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wide font-bold text-[#15736d] bg-[#e8f3f1] px-1.5 py-0.5 rounded">
                            {(a.activity_type || 'activity').replace(/_/g, ' ')}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {a.start_date ||
                              (a.activity_year
                                ? `${a.activity_year}${
                                    a.activity_month
                                      ? `-${String(a.activity_month).padStart(2, '0')}`
                                      : ''
                                  }`
                                : a.period_label || '')}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {a.title ||
                            (a.metrics_json && typeof a.metrics_json === 'object'
                              ? [
                                  a.metrics_json.shortname,
                                  a.metrics_json.main_topic,
                                ]
                                  .filter(Boolean)
                                  .map(String)
                                  .join(' — ')
                              : '') ||
                            (a.activity_type || 'Activity').replace(/_/g, ' ')}
                        </p>
                        {a.narrative && (
                          <p className="text-xs text-gray-700 mt-2 leading-relaxed whitespace-pre-line">
                            {a.narrative}
                          </p>
                        )}
                        {a.location_text && (
                          <p className="text-[11px] text-gray-500 mt-1">{a.location_text}</p>
                        )}
                        {a.metrics_json && typeof a.metrics_json === 'object' && (
                          <p className="text-xs text-gray-600 mt-2">
                            {a.metrics_json.count_users != null && (
                              <span>{String(a.metrics_json.count_users)} participants</span>
                            )}
                            {a.metrics_json.region_hint != null && (
                              <span>
                                {a.metrics_json.count_users != null ? ' · ' : ''}
                                {String(a.metrics_json.region_hint)}
                              </span>
                            )}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {!loading && data && data.linked_targets?.length > 0 && (
                <section>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">
                    {keyAreaLabel ? `${keyAreaLabel} indicators` : 'Linked indicators'}
                  </h3>
                  <ul className="space-y-2">
                    {data.linked_targets.map((t) => (
                      <li
                        key={t.target_id}
                        className="text-xs rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"
                      >
                        <div className="font-semibold text-gray-800">{t.indicator}</div>
                        <div className="text-gray-500 mt-0.5">
                          {t.progress_pct != null ? `${t.progress_pct}%` : 'No data'} ·{' '}
                          {t.status_band?.replace('_', ' ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CountryImpactDrawer;
